import { Dropzone, DropzoneEmptyState } from '@/components/kibo-ui/dropzone';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { useCreateAsset } from '@/features/assets';
import { useGenerateGraphic } from '@/features/graphics/api/generate-graphic';
import { useListServices } from '@/features/organization-services';
import { useUploadImage } from '@/features/upload';
import { logError } from '@/lib/log-error';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckCircle2Icon,
  CircleAlertIcon,
  ImagesIcon,
  Trash2Icon,
  UploadIcon,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

const MAX_SLIDES = 10;
const MAX_IMAGE_SIZE = 25 * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/webp': ['.webp'],
};

type SlideStatus = 'idle' | 'uploading' | 'uploaded' | 'error';

interface CarouselSlide {
  id: string;
  file: File;
  objectUrl: string;
  status: SlideStatus;
  assetId?: string;
}

export interface UploadCarouselDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: React.ReactNode | null;
  onSuccess?: () => void;
}

/**
 * Uploads finished image files, preserves their chosen order, then asks the
 * exact-image assembly worker to publish them as one organic carousel.
 */
export function UploadCarouselDialog({
  open: controlledOpen,
  onOpenChange,
  trigger,
  onSuccess,
}: UploadCarouselDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [serviceId, setServiceId] = useState('');
  const [slides, setSlides] = useState<CarouselSlide[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const objectUrlsRef = useRef(new Set<string>());
  const slideSequenceRef = useRef(0);

  const { services, isLoading: isLoadingServices } = useListServices({
    limit: 100,
  });
  const { uploadAsync: uploadImageAsync } = useUploadImage({
    purpose: 'org-asset',
    showToast: false,
  });
  const { createAssetAsync } = useCreateAsset({ showToast: false });
  const { generateGraphicAsync } = useGenerateGraphic();
  const open = controlledOpen ?? internalOpen;

  const setOpen = (next: boolean) => {
    if (controlledOpen === undefined) setInternalOpen(next);
    onOpenChange?.(next);
  };

  useEffect(
    () => () => {
      for (const url of objectUrlsRef.current) URL.revokeObjectURL(url);
    },
    []
  );

  const reset = () => {
    for (const url of objectUrlsRef.current) URL.revokeObjectURL(url);
    objectUrlsRef.current.clear();
    setTitle('');
    setServiceId('');
    setSlides([]);
    setIsSubmitting(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next && isSubmitting) return;
    if (!next) reset();
    setOpen(next);
  };

  const addSlides = (files: File[]) => {
    const available = MAX_SLIDES - slides.length;
    const accepted = files.slice(0, available);
    if (accepted.length < files.length) {
      toast.error(`A carousel can contain up to ${MAX_SLIDES} images.`);
    }
    if (accepted.length === 0) return;

    const nextSlides = accepted.map((file) => {
      const objectUrl = URL.createObjectURL(file);
      objectUrlsRef.current.add(objectUrl);
      slideSequenceRef.current += 1;
      return {
        id: `${file.name}-${file.lastModified}-${slideSequenceRef.current}`,
        file,
        objectUrl,
        status: 'idle' as const,
      };
    });
    setSlides((current) => [...current, ...nextSlides]);
  };

  const moveSlide = (index: number, offset: -1 | 1) => {
    setSlides((current) => {
      const destination = index + offset;
      if (destination < 0 || destination >= current.length) return current;
      const reordered = [...current];
      const [slide] = reordered.splice(index, 1);
      if (!slide) return current;
      reordered.splice(destination, 0, slide);
      return reordered;
    });
  };

  const removeSlide = (id: string) => {
    setSlides((current) => {
      const removed = current.find((slide) => slide.id === id);
      if (removed) {
        URL.revokeObjectURL(removed.objectUrl);
        objectUrlsRef.current.delete(removed.objectUrl);
      }
      return current.filter((slide) => slide.id !== id);
    });
  };

  const setSlideStatus = (
    id: string,
    status: SlideStatus,
    assetId?: string
  ) => {
    setSlides((current) =>
      current.map((slide) =>
        slide.id === id ? { ...slide, status, assetId } : slide
      )
    );
  };

  const uploadSlide = async (slide: CarouselSlide): Promise<string> => {
    if (slide.assetId) return slide.assetId;
    setSlideStatus(slide.id, 'uploading');
    try {
      const upload = await uploadImageAsync(slide.file);
      const asset = await createAssetAsync({
        file: slide.file,
        blobUrl: upload.url,
        type: 'image',
        source: 'raw',
      });
      setSlideStatus(slide.id, 'uploaded', asset.id);
      return asset.id;
    } catch (error) {
      setSlideStatus(slide.id, 'error');
      throw error;
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!serviceId || !title.trim() || slides.length < 2) return;

    setIsSubmitting(true);
    // The user's work is complete once they submit. Keep the network work in
    // this mounted component, but dismiss the form instead of replacing it
    // with another progress/completion modal.
    setOpen(false);
    try {
      const assetIds: string[] = [];
      // Two uploads at a time avoids freezing mobile browsers while preserving
      // the selected order in the result array.
      for (let index = 0; index < slides.length; index += 2) {
        const pair = slides.slice(index, index + 2);
        assetIds.push(...(await Promise.all(pair.map(uploadSlide))));
      }

      await generateGraphicAsync({
        serviceId,
        kind: 'carousel',
        imageMode: 'assemble',
        topicSummary: title.trim(),
        sourceAssetIds: assetIds,
        usageType: 'organic',
        allowAiImages: false,
        allowStockImages: false,
      });

      reset();
      onSuccess?.();
    } catch (error) {
      logError('graphics.uploadCarousel', error, {
        feature: 'graphics',
        extra: { slideCount: slides.length },
      });
      toast.error(
        error instanceof Error ? error.message : 'Failed to upload carousel'
      );
      setIsSubmitting(false);
    }
  };

  const canSubmit =
    !isSubmitting &&
    title.trim().length > 0 &&
    serviceId.length > 0 &&
    slides.length >= 2;
  const hasFailedSlides = slides.some((slide) => slide.status === 'error');

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        {trigger !== null ? (
          <DialogTrigger asChild>
            {trigger ?? (
              <Button variant="outline">
                <ImagesIcon data-icon="inline-start" />
                Upload carousel
              </Button>
            )}
          </DialogTrigger>
        ) : null}
        <DialogContent
          className="max-h-[90vh] overflow-y-auto sm:max-w-2xl"
          showCloseButton={!isSubmitting}
          onEscapeKeyDown={(event) => {
            if (isSubmitting) event.preventDefault();
          }}
          onPointerDownOutside={(event) => {
            if (isSubmitting) event.preventDefault();
          }}
        >
          <form onSubmit={handleSubmit} className="flex flex-col gap-6">
            <DialogHeader>
              <DialogTitle>Upload a carousel</DialogTitle>
              <DialogDescription>
                Add 2–10 finished images. They will stay unchanged and appear in
                the order shown below.
              </DialogDescription>
            </DialogHeader>

            <FieldGroup className="gap-5">
              <Field>
                <FieldLabel htmlFor="carousel-title">Carousel title</FieldLabel>
                <Input
                  id="carousel-title"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  placeholder="e.g. Five ways to care for your skin"
                  maxLength={90}
                  disabled={isSubmitting}
                  required
                />
              </Field>

              <Field>
                <FieldLabel htmlFor="carousel-service">Service</FieldLabel>
                <Select
                  value={serviceId}
                  onValueChange={setServiceId}
                  disabled={isSubmitting || isLoadingServices}
                  required
                >
                  <SelectTrigger
                    id="carousel-service"
                    className="w-full"
                    aria-label="Service"
                  >
                    <SelectValue
                      placeholder={
                        isLoadingServices
                          ? 'Loading services…'
                          : 'Choose a service'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {services.map((service) => (
                        <SelectItem key={service.id} value={service.id}>
                          {service.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <FieldDescription>
                  This keeps the carousel organised with the content it
                  promotes.
                </FieldDescription>
              </Field>

              <Field>
                <FieldLabel>Slides</FieldLabel>
                <Dropzone
                  accept={ACCEPTED_IMAGE_TYPES}
                  maxFiles={MAX_SLIDES}
                  maxSize={MAX_IMAGE_SIZE}
                  onDrop={addSlides}
                  onError={(error) => toast.error(error.message)}
                  disabled={isSubmitting || slides.length >= MAX_SLIDES}
                  className="min-h-28 border-dashed"
                >
                  <DropzoneEmptyState>
                    <div className="flex flex-col items-center gap-2 text-center">
                      <UploadIcon />
                      <p className="text-sm font-medium">
                        {slides.length === 0
                          ? 'Choose carousel images'
                          : 'Add more images'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        JPG, PNG or WebP · {slides.length}/{MAX_SLIDES} selected
                      </p>
                    </div>
                  </DropzoneEmptyState>
                </Dropzone>
                <FieldDescription>
                  The first image becomes the cover. Use the arrows to change
                  the order before uploading.
                </FieldDescription>
              </Field>
            </FieldGroup>

            {slides.length > 0 ? (
              <ol className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5">
                {slides.map((slide, index) => (
                  <li key={slide.id} className="flex min-w-0 flex-col gap-2">
                    <div className="relative aspect-[4/5] overflow-hidden rounded-lg border bg-muted">
                      <img
                        src={slide.objectUrl}
                        alt={`Slide ${index + 1}: ${slide.file.name}`}
                        className="size-full object-cover"
                      />
                      <span className="absolute left-2 top-2 flex size-6 items-center justify-center rounded-full bg-background text-xs font-semibold shadow-sm">
                        {index + 1}
                      </span>
                      {slide.status === 'uploading' ? (
                        <span className="absolute inset-0 flex items-center justify-center bg-background/70">
                          <Spinner />
                        </span>
                      ) : null}
                      {slide.status === 'uploaded' ? (
                        <span className="absolute right-2 top-2 flex size-6 items-center justify-center rounded-full bg-background shadow-sm">
                          <CheckCircle2Icon className="text-primary" />
                        </span>
                      ) : null}
                      {slide.status === 'error' ? (
                        <Badge
                          variant="destructive"
                          className="absolute inset-x-1 bottom-1 justify-center gap-1 px-1.5 py-1 text-[10px]"
                          role="status"
                        >
                          <CircleAlertIcon />
                          Upload failed
                        </Badge>
                      ) : null}
                    </div>
                    <p className="truncate text-xs text-muted-foreground">
                      {slide.file.name}
                    </p>
                    <div className="flex items-center justify-center gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Move slide ${index + 1} left`}
                        disabled={isSubmitting || index === 0}
                        onClick={() => moveSlide(index, -1)}
                      >
                        <ArrowLeftIcon />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Move slide ${index + 1} right`}
                        disabled={isSubmitting || index === slides.length - 1}
                        onClick={() => moveSlide(index, 1)}
                      >
                        <ArrowRightIcon />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Remove slide ${index + 1}`}
                        disabled={isSubmitting}
                        onClick={() => removeSlide(slide.id)}
                      >
                        <Trash2Icon />
                      </Button>
                    </div>
                  </li>
                ))}
              </ol>
            ) : null}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                disabled={isSubmitting}
                onClick={() => handleOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={!canSubmit}>
                {isSubmitting ? (
                  <Spinner data-icon="inline-start" />
                ) : (
                  <ImagesIcon data-icon="inline-start" />
                )}
                {isSubmitting
                  ? 'Uploading…'
                  : hasFailedSlides
                    ? 'Retry upload'
                    : 'Upload carousel'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
